import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useApi } from "../../utils/api";
import {
  getYouTubeEmbedUrl,
  isYouTubeVideo,
} from "../../utils/youtubeVideo";

const VideoPlayerModal = ({ open, onClose, videoKey, videoName }) => {
  const { getSignedUrl } = useApi();
  const [videoUrl, setVideoUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const videoRef = useRef(null);
  const isYouTube = isYouTubeVideo(videoKey);
  const youtubeEmbedUrl = isYouTube ? getYouTubeEmbedUrl(videoKey) : null;

  useEffect(() => {
    const fetchVideoUrl = async () => {
      if (!videoKey || isYouTubeVideo(videoKey)) return;

      setIsLoading(true);
      setError(null);
      try {
        const { downloadUrl } = await getSignedUrl(videoKey);
        setVideoUrl(downloadUrl);
      } catch (err) {
        console.error("Error fetching video URL:", err);
        setError("Failed to load video");
      } finally {
        setIsLoading(false);
      }
    };

    if (open && videoKey) {
      fetchVideoUrl();
    }
  }, [open, videoKey, getSignedUrl]);

  useEffect(() => {
    if (!open) {
      setVideoUrl(null);
      setError(null);
      setIsLoading(false);
    }
  }, [open]);

  useEffect(() => {
    if (videoRef.current && videoUrl) {
      videoRef.current.load();
    }
  }, [videoUrl]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          height: "90vh",
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{videoName}</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {isYouTube ? (
            youtubeEmbedUrl ? (
              <Box
                component="iframe"
                src={youtubeEmbedUrl}
                title={videoName || "YouTube video"}
                referrerPolicy="strict-origin-when-cross-origin"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                sx={{
                  width: "100%",
                  height: "100%",
                  minHeight: 360,
                  border: 0,
                }}
              />
            ) : (
              <Typography color="error">Invalid YouTube video</Typography>
            )
          ) : isLoading ? (
            <CircularProgress />
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : (
            <video
              ref={videoRef}
              controls
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                width: "auto",
                height: "auto",
              }}
            >
              {videoUrl && <source src={videoUrl} type="video/mp4" />}
              Your browser does not support the video tag.
            </video>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default VideoPlayerModal;
